export interface OutputAnalysisStructure {
	debtCapacity: number;
	proformaDebt: number;
	existingDebt: number;
	incrementalDebt: number;
	remainingDebt: number;
	refinancedDebt: number;
	header: CreditOutputAnalysis | null;
	items: CreditOutputLineItem[];
	outputItems: OutputAnalysisItem[];
}

export interface CreditOutputAnalysis {
	id: number;
	guid: string;
	debtCapacity: number | null;
	isProfitLossSectionVisible: boolean;
	isCashFlowSectionVisible: boolean;
	isBalanceSheetSectionVisible: boolean;
	isBusinessSectionVisible: boolean;
}

export interface CreditOutputLineItem {
	id: number;
	guid: string;
	displayIndex: number;
	groupType: OutputAnalysisGroup;
	outputType: OutputAnalysisType;
	outputStatus: OutputAnalysisStatus;
	summaryStatus: SummaryOutputStatus;
	comments: string;
	isVisibleToBorrower: boolean;
}

export interface OutputAnalysisItem {
	displayIndex: number;
	groupType: OutputAnalysisGroup;
	outputType: OutputAnalysisType;
	outputStatus: OutputAnalysisStatus;
	summaryStatus: SummaryOutputStatus;
	value: string;
	comments: string;
	variables: OutputAnalysisItemVariable[];
	ratings: OutputAnalysisRating[];
	formulas: OutputAnalysisFormula[];
}

export interface OutputAnalysisRating {
	outputStatus: OutputAnalysisStatus;
	conditions: string;
}

export interface OutputAnalysisFormula {
	name: string;
	conditions: string;
}

export interface OutputAnalysisItemVariable {
	definition: string;
	value: string;
}

export type OutputAnalysisType = keyof typeof OutputAnalysisTypeMap;
export const OutputAnalysisTypeMap = {
	TtmRevenue: 'TTM Revenue',
	HistoricalRevenueTrend: 'Historical Revenue Trend',
	ProjectedRevenueTrend: 'Projected Revenue Trend',
	CurrentNetIncomeMargin: 'Current Net Income Margin',
	HistoricalNetIncomeMarginTrends: 'Historical Net Income Margin Trends',
	ProjectedNetIncomeMarginTrends: 'Projected Net Income Margin Trends',
	EbitdaAddbackPercentage: 'EBITDA Addback Percentage',
	CurrentEbitdaMargin: 'Current EBITDA Margin',
	CurrentEbitdaMarginTrends: 'Current EBITDA Margin Trends',
	CurrentEbitda: 'Current EBITDA',
	CurrentCashFlowLeverage: 'Current Cash Flow Leverage',
	HistoricalEbitdaTrend: 'Historical EBITDA Trend',
	ProjectedEbitdaTrend: 'Projected EBITDA Trend',
	CurrentLiquidityProfile: 'Current Liquidity Profile',
	CurrentVariableCollateralProfile: 'Current Variable Collateral Profile',
	CurrentFixedCollateralProfile: 'Current Fixed Collateral Profile',
	Headcount: 'Total Headcount',
	HeadcountMix: 'US Headcount Percentage',
	CashRunway: 'Cash Runway',
	FinancialStatements: 'Financial Statements',
	Industry: 'Industry',
	ProformaTotalDebtToAdjustmentEbitda: 'Proforma Debt Relative to Adj. EBITDA',
	ProformaTotalDebtToRevenue: 'Proforma Debt Relative to Revenue',
	CashInfusion: 'Cash Infusion',
	ProformaLiquidityProfile: 'Liquidity Profile',
	ProformaVariableCollateralProfile: 'Variable Collateral Profile',
	ProformaFixedCollateralProfile: 'Fixed Collateral Profile',
	Predictability: 'Predictability',
	FinanceConfidence: 'Finance Confidence',
	LendersInterested: 'Number of Lenders Interested',
	RefinancingExistingDebt: 'Probability in Refinancing Exsiting Debt with Better Terms',
	IncrementalNewDebt: 'Estimated Amount of Incremental New Debt',
};

export type OutputAnalysisStatus = keyof typeof OutputAnalysisStatusMap;
export const OutputAnalysisStatusMap = {
	MissingData: 'Missing Data',
	VeryStrong: 'Very Strong / Low Risk',
	Strong: 'Strong',
	Neutral: 'Stable / Neutral',
	Weak: 'Weak / High Risk',
};

export type SummaryOutputStatus = keyof typeof SummaryOutputStatusMap;
export const SummaryOutputStatusMap = {
	None: 'None',
	VeryHigh: 'Very High',
	High: 'High',
	Moderate: 'Moderate',
	Low: 'Low',
	VeryLow: 'Very Low',
};

export type OutputAnalysisGroup = keyof typeof OutputAnalysisGroupMap;
export const OutputAnalysisGroupMap = {
	None: 'N/A',
	Summary: 'Summary',
	ProfitLoss: 'P&L',
	CashFlows: 'Cash Flows',
	BalanceSheet: 'Balance Sheet',
	Business: 'Business',
	LoanRequest: 'Loan Request',
};
